// FIX: Import missing React hooks and types.
import React, { useState, useEffect, ChangeEvent, useRef, useMemo, FormEvent, useCallback } from 'react';
import CryptoJS from 'crypto-js';
import PageHeader from '../PageHeader';
import { AdmissionData, Attachment, NewAttachment, NewSignature, SavablePageProps, Signature } from '../../types';
import { API_URL, API_KEY_EN } from '../../user_constant';

interface AdmissionPageProps extends SavablePageProps {
  onContinue: () => void;
}

// Reusable component for section titles and layout
const Section: React.FC<{ title: React.ReactNode; children: React.ReactNode; className?: string }> = ({ title, children, className = '' }) => (
    <div className={`py-8 border-b border-slate-200 last:border-b-0 print:border-none print:py-4 print:break-inside-avoid ${className}`}>
        <h3 className="text-xl font-semibold text-slate-800 mb-6 print:mb-3 print:text-lg">{title}</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-6 print:grid-cols-3 print:gap-y-2 print:gap-x-4">
            {children}
        </div>
    </div>
);

// Reusable component for form input fields
const FormField: React.FC<{
    label: string;
    name: string;
    type?: string;
    placeholder?: string;
    fullWidth?: boolean;
    readOnly?: boolean;
    value?: string | number;
    onChange?: (e: ChangeEvent<HTMLInputElement>) => void;
    highlight?: boolean;
    isPrintView?: boolean;
    isStaffInput?: boolean;
}> = ({ label, name, type = 'text', placeholder = '', fullWidth = false, readOnly = false, value, onChange, highlight = false, isPrintView = false, isStaffInput = false }) => {
    const isDate = type === 'date';
    const hasValue = value !== undefined && String(value).length > 0;
    
    if (isPrintView) {
        return (
            <div className={`${fullWidth ? 'md:col-span-2 lg:col-span-3' : ''} print:mb-1`}>
                <label className="block text-sm font-semibold text-slate-700 mb-1 print:mb-0 print:text-xs print:text-slate-500">{label}</label>
                <div className="text-sm text-slate-900 p-2 min-h-[38px] bg-slate-50 rounded-md break-words print:p-0 print:min-h-0 print:bg-transparent print:border-b print:border-slate-300 print:rounded-none print:pb-1 print:leading-snug">
                    {value || '-'}
                </div>
            </div>
        );
    }
    
    const baseBorder = isStaffInput ? 'border-brand-primary border-2 shadow-md' : 'border-slate-300 border shadow-sm hover:border-slate-400';
    const baseClasses = `block w-full px-4 py-2 ${baseBorder} rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-primary/80 focus:border-brand-primary`;
    const baseBorderStyle = { transition: 'background-color 0.2s, box-shadow 0.2s, color 0.2s' };
    const statefulClasses = readOnly
        ? "bg-slate-100 text-slate-500 cursor-not-allowed border-slate-300 hover:border-slate-300"
        : `bg-white placeholder-slate-400 ${
            isDate ? (hasValue ? 'text-slate-900' : 'text-slate-400') : 'text-slate-900'
          }`;
    const autoHighlight = !readOnly && !isPrintView && !hasValue;
    const inputClasses = `${baseClasses} ${statefulClasses} ${(highlight || autoHighlight) ? 'animate-pulse-highlight' : ''}`;

    return (
        <div className={fullWidth ? 'md:col-span-2 lg:col-span-3' : ''}>
            <label htmlFor={name} className="block text-sm font-semibold text-slate-700 mb-2">{label}</label>
            <div className="relative">
                <input
                    id={name}
                    name={name}
                    type={type}
                    placeholder={placeholder}
                    readOnly={readOnly}
                    value={value}
                    onChange={onChange}
                    className={inputClasses}
                    style={baseBorderStyle}
                />
                {isDate && (
                    <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                        <svg className="h-5 w-5 text-slate-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                        </svg>
                    </div>
                )}
            </div>
        </div>
    );
};

// Reusable Address Field with Google Maps Autocomplete
const AddressField: React.FC<{
    label: string;
    name: string;
    fullWidth?: boolean;
    readOnly?: boolean;
    value: string;
    onChange: (value: string) => void;
    highlight?: boolean;
    isPrintView?: boolean;
}> = ({ label, name, fullWidth = false, readOnly = false, value, onChange, highlight = false, isPrintView = false }) => {
    const inputRef = useRef<HTMLInputElement>(null);
    const [authError, setAuthError] = useState(false);

    useEffect(() => {
        const handleAuthError = () => setAuthError(true);
        document.addEventListener('google-maps-auth-error', handleAuthError);
        return () => document.removeEventListener('google-maps-auth-error', handleAuthError);
    }, []);

    useEffect(() => {
        if (isPrintView || readOnly || !inputRef.current) {
            return;
        }

        let autocomplete: any = null;
        let intervalId: number | null = null;
        let attempts = 0;

        const initAutocomplete = () => {
            attempts++;
            if ((window as any).google?.maps?.places && inputRef.current) {
                if (intervalId) {
                    clearInterval(intervalId);
                }
                
                autocomplete = new (window as any).google.maps.places.Autocomplete(inputRef.current, {
                    componentRestrictions: { country: "my" },
                    fields: ["formatted_address"],
                    types: ["address"],
                });

                autocomplete.addListener("place_changed", () => {
                    const place = autocomplete.getPlace();
                    if (place?.formatted_address) {
                        onChange(place.formatted_address);
                    }
                });

                if (navigator.geolocation) {
                    navigator.geolocation.getCurrentPosition((position) => {
                        const geolocation = {
                            lat: position.coords.latitude,
                            lng: position.coords.longitude,
                        };
                        const circle = new (window as any).google.maps.Circle({
                            center: geolocation,
                            radius: position.coords.accuracy,
                        });
                        autocomplete.setBounds(circle.getBounds());
                    }, (error) => {
                        console.warn(`Geolocation error: ${error.message}`);
                    });
                }
            } else if (attempts > 20) { // stop after 10 seconds
                 if (intervalId) {
                    clearInterval(intervalId);
                }
                console.warn("Google Maps script could not be loaded. Address autocomplete is disabled.");
            }
        };
        
        if ((window as any).google?.maps?.places) {
            initAutocomplete();
        } else {
            intervalId = window.setInterval(initAutocomplete, 500);
        }

        return () => {
            if (intervalId) {
                clearInterval(intervalId);
            }
            if ((window as any).google?.maps?.event && autocomplete) {
                (window as any).google.maps.event.clearInstanceListeners(autocomplete);
            }
        };
    }, [isPrintView, readOnly, onChange]);

    if (isPrintView) {
        return (
            <div className={`${fullWidth ? 'md:col-span-2 lg:col-span-3' : ''} print:mb-1`}>
                <label className="block text-sm font-semibold text-slate-700 mb-1 print:mb-0 print:text-xs print:text-slate-500">{label}</label>
                <div className="text-sm text-slate-900 p-2 min-h-[38px] bg-slate-50 rounded-md whitespace-pre-wrap break-words print:p-0 print:min-h-0 print:bg-transparent print:border-b print:border-slate-300 print:rounded-none print:pb-1 print:leading-snug">{value || '-'}</div>
            </div>
        );
    }
    
    const baseClasses = "block w-full px-4 py-2 border border-slate-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-brand-primary/80 focus:border-brand-primary transition-all duration-200";
    const statefulClasses = readOnly
        ? "bg-slate-100 text-slate-500 cursor-not-allowed"
        : `bg-white placeholder-slate-400 hover:border-slate-400 text-slate-900`;
    const autoHighlight = !readOnly && !isPrintView && !value.trim();
    const inputClasses = `${baseClasses} ${statefulClasses} ${(highlight || autoHighlight) ? 'animate-pulse-highlight' : ''}`;

    return (
        <div className={fullWidth ? 'md:col-span-2 lg:col-span-3' : ''}>
            <label htmlFor={name} className="block text-sm font-semibold text-slate-700 mb-2">{label}</label>
            {authError && (
                <div className="mb-2 p-3 bg-red-50 border border-red-200 text-red-800 rounded-lg text-sm" role="alert">
                    <div className="flex">
                        <div className="flex-shrink-0">
                            <svg className="h-5 w-5 text-red-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.28 7.22a.75.75 0 00-1.06 1.06L8.94 10l-1.72 1.72a.75.75 0 101.06 1.06L10 11.06l1.72 1.72a.75.75 0 101.06-1.06L11.06 10l1.72-1.72a.75.75 0 00-1.06-1.06L10 8.94 8.28 7.22z" clipRule="evenodd" />
                            </svg>
                        </div>
                        <div className="ml-3">
                            <h3 className="text-sm font-bold text-red-900">Address Autocomplete is Inactive</h3>
                            <div className="mt-2 text-sm text-red-800">
                                <p>The "Places API" is not enabled for this project's API key. A project administrator must enable it in the Google Cloud Console for this feature to work.</p>
                            </div>
                        </div>
                    </div>
                </div>
            )}
            <input
                ref={inputRef}
                id={name}
                name={name}
                type="text"
                placeholder={authError ? "Address autocomplete is disabled" : "Start typing an address..."}
                readOnly={readOnly || authError}
                value={value}
                onChange={(e) => onChange(e.target.value)}
                className={`${inputClasses} ${authError ? 'bg-slate-100 cursor-not-allowed placeholder:text-slate-400' : ''}`}
            />
        </div>
    );
};


// Reusable component for textarea fields
const TextAreaField: React.FC<{
    label: string;
    name: string;
    placeholder?: string;
    fullWidth?: boolean;
    rows?: number;
    value?: string;
    onChange?: (e: ChangeEvent<HTMLTextAreaElement>) => void;
    highlight?: boolean;
    isPrintView?: boolean;
}> = ({ label, name, placeholder = '', fullWidth = false, rows = 3, value, onChange, highlight = false, isPrintView = false }) => {
    if (isPrintView) {
        return (
            <div className={`${fullWidth ? 'md:col-span-2 lg:col-span-3' : ''} print:mb-1`}>
                <label className="block text-sm font-semibold text-slate-700 mb-1 print:mb-0 print:text-xs print:text-slate-500">{label}</label>
                <div className="text-sm text-slate-900 p-2 min-h-[38px] bg-slate-50 rounded-md whitespace-pre-wrap break-words print:p-0 print:min-h-0 print:bg-transparent print:border-b print:border-slate-300 print:rounded-none print:pb-1 print:leading-snug">{value || '-'}</div>
            </div>
        );
    }
    
    const autoHighlight = !isPrintView && !value?.trim();
    return (
     <div className={fullWidth ? 'md:col-span-2 lg:col-span-3' : ''}>
        <label htmlFor={name} className="block text-sm font-semibold text-slate-700 mb-2">{label}</label>
        <textarea
            id={name}
            name={name}
            rows={rows}
            placeholder={placeholder}
            value={value}
            onChange={onChange}
            className={`block w-full px-4 py-2 bg-white text-slate-900 border border-slate-300 rounded-lg shadow-sm placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-brand-primary/80 focus:border-brand-primary hover:border-slate-400 transition-all duration-200 ${(highlight || autoHighlight) ? 'animate-pulse-highlight' : ''}`}
        />
    </div>
    );
}

// Component for Radio button groups
const RadioGroupField: React.FC<{
    label: string;
    name: string;
    options: string[];
    fullWidth?: boolean;
    value: string;
    onChange: (e: ChangeEvent<HTMLInputElement>) => void;
    highlight?: boolean;
    isPrintView?: boolean;
}> = ({ label, name, options, fullWidth = false, value, onChange, highlight = false, isPrintView = false }) => {
    if (isPrintView) {
        return (
            <div className={`${fullWidth ? 'md:col-span-2 lg:col-span-3' : ''} print:mb-1`}>
                <label className="block text-sm font-semibold text-slate-700 mb-1 print:mb-0 print:text-xs print:text-slate-500">{label}</label>
                <div className="text-sm text-slate-900 p-2 min-h-[38px] bg-slate-50 rounded-md print:p-0 print:min-h-0 print:bg-transparent print:border-b print:border-slate-300 print:rounded-none print:pb-1 print:leading-snug">{value || '-'}</div>
            </div>
        );
    }
    const autoHighlight = !isPrintView && !value;
    return (
    <div className={fullWidth ? 'md:col-span-2 lg:col-span-3' : ''}>
        <label className="block text-sm font-semibold text-slate-700 mb-2">{label}</label>
        <div className={`flex items-center space-x-2 rounded-lg bg-slate-100 p-1 ${(highlight || autoHighlight) ? 'animate-pulse-highlight' : ''}`}>
            {options.map((option) => (
                <label key={option} className="relative w-full flex-1 cursor-pointer">
                    <input
                        id={`${name}-${option.toLowerCase()}`}
                        name={name}
                        type="radio"
                        value={option}
                        className="peer sr-only"
                        checked={value?.toLowerCase() === option.toLowerCase()}
                        onChange={onChange}
                    />
                    <span className="block w-full text-center py-2 px-3 rounded-md text-sm font-medium text-slate-600 transition-all duration-200 ease-in-out hover:bg-slate-200 peer-checked:bg-brand-primary peer-checked:text-white peer-checked:shadow-sm">
                        {option}
                    </span>
                </label>
            ))}
        </div>
    </div>
    );
}

// Component for Checkbox groups (multi-select)
const CheckboxGroupField: React.FC<{
    label: string;
    name: string;
    options: string[];
    fullWidth?: boolean;
    value: string[];
    onChange: (selected: string[]) => void;
    highlight?: boolean;
    isPrintView?: boolean;
}> = ({ label, name, options, fullWidth = false, value, onChange, highlight = false, isPrintView = false }) => {
    if (isPrintView) {
        return (
             <div className={`${fullWidth ? 'md:col-span-2 lg:col-span-3' : ''} print:mb-1`}>
                <label className="block text-sm font-semibold text-slate-700 mb-1 print:mb-0 print:text-xs print:text-slate-500">{label}</label>
                <div className="text-sm text-slate-900 p-2 min-h-[38px] bg-slate-50 rounded-md print:p-0 print:min-h-0 print:bg-transparent print:border-b print:border-slate-300 print:rounded-none print:pb-1 print:leading-snug">{value.join(', ') || '-'}</div>
            </div>
        );
    }
    const handleChange = (option: string) => {
        const newSelection = [...value];
        if (newSelection.includes(option)) {
            newSelection.splice(newSelection.indexOf(option), 1);
        } else {
            newSelection.push(option);
        }
        onChange(newSelection);
    };
    
    const autoHighlight = !isPrintView && value.length === 0;
    return (
    <div className={fullWidth ? 'md:col-span-2 lg:col-span-3' : ''}>
        <label className="block text-sm font-semibold text-slate-700 mb-3">{label}</label>
        <div className={`flex flex-wrap items-center gap-x-6 gap-y-3 p-2 rounded-lg ${(highlight || autoHighlight) ? 'animate-pulse-highlight' : ''}`}>
            {options.map((option) => (
                <label key={option} htmlFor={`${name}-${option.toLowerCase()}`} className="flex items-center cursor-pointer group">
                    <input
                        id={`${name}-${option.toLowerCase()}`}
                        name={`${name}[]`}
                        type="checkbox"
                        value={option}
                        className="sr-only peer"
                        checked={value.includes(option)}
                        onChange={() => handleChange(option)}
                    />
                    <div className="w-5 h-5 mr-2.5 flex-shrink-0 border-2 border-slate-300 rounded-md group-hover:border-brand-primary peer-checked:bg-brand-primary peer-checked:border-brand-primary transition-all duration-200 flex items-center justify-center">
                        <svg className="w-3 h-3 text-white fill-current opacity-0 peer-checked:opacity-100 transition-opacity duration-200 transform scale-50 peer-checked:scale-100" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M13.1926 4.20233L6.42591 10.969L2.80738 7.35046" stroke="white" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                    </div>
                    <span className="block text-sm font-medium text-slate-700 group-hover:text-slate-900">
                        {option}
                    </span>
                </label>
            ))}
        </div>
    </div>
);
}

// Data and Component for Phone input with country code
const countries = [ { name: 'Malaysia', code: '+60', flag: '🇲🇾' }, { name: 'Singapore', code: '+65', flag: '🇸🇬' }, { name: 'Afghanistan', code: '+93', flag: '🇦🇫' }, { name: 'Albania', code: '+355', flag: '🇦🇱' }, { name: 'Algeria', code: '+213', flag: '🇩🇿' }, { name: 'American Samoa', code: '+1684', flag: '🇦🇸' }, { name: 'Andorra', code: '+376', flag: '🇦🇩' }, { name: 'Angola', code: '+244', flag: '🇦🇴' }, { name: 'Anguilla', code: '+1264', flag: '🇦🇮' }, { name: 'Antarctica', code: '+672', flag: '🇦🇶' }, { name: 'Antigua and Barbuda', code: '+1268', flag: '🇦🇬' }, { name: 'Argentina', code: '+54', flag: '🇦🇷' }, { name: 'Armenia', code: '+374', flag: '🇦🇲' }, { name: 'Aruba', code: '+297', flag: '🇦🇼' }, { name: 'Australia', code: '+61', flag: '🇦🇺' }, { name: 'Austria', code: '+43', flag: '🇦🇹' }, { name: 'Azerbaijan', code: '+994', flag: '🇦🇿' }, { name: 'Bahamas', code: '+1242', flag: '🇧🇸' }, { name: 'Bahrain', code: '+973', flag: '🇧🇭' }, { name: 'Bangladesh', code: '+880', flag: '🇧🇩' }, { name: 'Barbados', code: '+1246', flag: '🇧🇧' }, { name: 'Belarus', code: '+375', flag: '🇧🇾' }, { name: 'Belgium', code: '+32', flag: '🇧🇪' }, { name: 'Belize', code: '+501', flag: '🇧🇿' }, { name: 'Benin', code: '+229', flag: '🇧🇯' }, { name: 'Bermuda', code: '+1441', flag: '🇧🇲' }, { name: 'Bhutan', code: '+975', flag: '🇧🇹' }, { name: 'Bolivia', code: '+591', flag: '🇧🇴' }, { name: 'Bosnia and Herzegovina', code: '+387', flag: '🇧🇦' }, { name: 'Botswana', code: '+267', flag: '🇧🇼' }, { name: 'Brazil', code: '+55', flag: '🇧🇷' }, { name: 'British Indian Ocean Territory', code: '+246', flag: '🇮🇴' }, { name: 'British Virgin Islands', code: '+1284', flag: '🇻🇬' }, { name: 'Brunei', code: '+673', flag: '🇧🇳' }, { name: 'Bulgaria', code: '+359', flag: '🇧🇬' }, { name: 'Burkina Faso', code: '+226', flag: '🇧🇫' }, { name: 'Burundi', code: '+257', flag: '🇧🇮' }, { name: 'Cambodia', code: '+855', flag: '🇰🇭' }, { name: 'Cameroon', code: '+237', flag: '🇨🇲' }, { name: 'Canada', code: '+1', flag: '🇨🇦' }, { name: 'Cape Verde', code: '+238', flag: '🇨🇻' }, { name: 'Cayman Islands', code: '+1345', flag: '🇰🇾' }, { name: 'Central African Republic', code: '+236', flag: '🇨🇫' }, { name: 'Chad', code: '+235', flag: '🇹🇩' }, { name: 'Chile', code: '+56', flag: '🇨🇱' }, { name: 'China', code: '+86', flag: '🇨🇳' }, { name: 'Christmas Island', code: '+61', flag: '🇨🇽' }, { name: 'Cocos Islands', code: '+61', flag: '🇨🇨' }, { name: 'Colombia', code: '+57', flag: '🇨🇴' }, { name: 'Comoros', code: '+269', flag: '🇰🇲' }, { name: 'Cook Islands', code: '+682', flag: '🇨🇰' }, { name: 'Costa Rica', code: '+506', flag: '🇨🇷' }, { name: 'Croatia', code: '+385', flag: '🇭🇷' }, { name: 'Cuba', code: '+53', flag: '🇨🇺' }, { name: 'Curacao', code: '+599', flag: '🇨🇼' }, { name: 'Cyprus', code: '+357', flag: '🇨🇾' }, { name: 'Czech Republic', code: '+420', flag: '🇨🇿' }, { name: 'Democratic Republic of the Congo', code: '+243', flag: '🇨🇩' }, { name: 'Denmark', code: '+45', flag: '🇩🇰' }, { name: 'Djibouti', code: '+253', flag: '🇩🇯' }, { name: 'Dominica', code: '+1767', flag: '🇩🇲' }, { name: 'Dominican Republic', code: '+1', flag: '🇩🇴' }, { name: 'East Timor', code: '+670', flag: '🇹🇱' }, { name: 'Ecuador', code: '+593', flag: '🇪🇨' }, { name: 'Egypt', code: '+20', flag: '🇪🇬' }, { name: 'El Salvador', code: '+503', flag: '🇸🇻' }, { name: 'Equatorial Guinea', code: '+240', flag: '🇬🇶' }, { name: 'Eritrea', code: '+291', flag: '🇪🇷' }, { name: 'Estonia', code: '+372', flag: '🇪🇪' }, { name: 'Ethiopia', code: '+251', flag: '🇪🇹' }, { name: 'Falkland Islands', code: '+500', flag: '🇫🇰' }, { name: 'Faroe Islands', code: '+298', flag: '🇫🇴' }, { name: 'Fiji', code: '+679', flag: '🇫🇯' }, { name: 'Finland', code: '+358', flag: '🇫🇮' }, { name: 'France', code: '+33', flag: '🇫🇷' }, { name: 'French Polynesia', code: '+689', flag: '🇵🇫' }, { name: 'Gabon', code: '+241', flag: '🇬🇦' }, { name: 'Gambia', code: '+220', flag: '🇬🇲' }, { name: 'Georgia', code: '+995', flag: '🇬🇪' }, { name: 'Germany', code: '+49', flag: '🇩🇪' }, { name: 'Ghana', code: '+233', flag: '🇬🇭' }, { name: 'Gibraltar', code: '+350', flag: '🇬🇮' }, { name: 'Greece', code: '+30', flag: '🇬🇷' }, { name: 'Greenland', code: '+299', flag: '🇬🇱' }, { name: 'Grenada', code: '+1473', flag: '🇬🇩' }, { name: 'Guam', code: '+1671', flag: '🇬🇺' }, { name: 'Guatemala', code: '+502', flag: '🇬🇹' }, { name: 'Guernsey', code: '+44', flag: '🇬🇬' }, { name: 'Guinea', code: '+224', flag: '🇬🇳' }, { name: 'Guinea-Bissau', code: '+245', flag: '🇬🇼' }, { name: 'Guyana', code: '+592', flag: '🇬🇾' }, { name: 'Haiti', code: '+509', flag: '🇭🇹' }, { name: 'Honduras', code: '+504', flag: '🇭🇳' }, { name: 'Hong Kong', code: '+852', flag: '🇭🇰' }, { name: 'Hungary', code: '+36', flag: '🇭🇺' }, { name: 'Iceland', code: '+354', flag: '🇮🇸' }, { name: 'India', code: '+91', flag: '🇮🇳' }, { name: 'Indonesia', code: '+62', flag: '🇮🇩' }, { name: 'Iran', code: '+98', flag: '🇮🇷' }, { name: 'Iraq', code: '+964', flag: '🇮🇶' }, { name: 'Ireland', code: '+353', flag: '🇮🇪' }, { name: 'Isle of Man', code: '+44', flag: '🇮🇲' }, { name: 'Israel', code: '+972', flag: '🇮🇱' }, { name: 'Italy', code: '+39', flag: '🇮🇹' }, { name: 'Ivory Coast', code: '+225', flag: '🇨🇮' }, { name: 'Jamaica', code: '+1876', flag: '🇯🇲' }, { name: 'Japan', code: '+81', flag: '🇯🇵' }, { name: 'Jersey', code: '+44', flag: '🇯🇪' }, { name: 'Jordan', code: '+962', flag: '🇯🇴' }, { name: 'Kazakhstan', code: '+7', flag: '🇰🇿' }, { name: 'Kenya', code: '+254', flag: '🇰🇪' }, { name: 'Kiribati', code: '+686', flag: '🇰🇮' }, { name: 'Kosovo', code: '+383', flag: '🇽🇰' }, { name: 'Kuwait', code: '+965', flag: '🇰🇼' }, { name: 'Kyrgyzstan', code: '+996', flag: '🇰🇬' }, { name: 'Laos', code: '+856', flag: '🇱🇦' }, { name: 'Latvia', code: '+371', flag: '🇱🇻' }, { name: 'Lebanon', code: '+961', flag: '🇱🇧' }, { name: 'Lesotho', code: '+266', flag: '🇱🇸' }, { name: 'Liberia', code: '+231', flag: '🇱🇷' }, { name: 'Libya', code: '+218', flag: '🇱🇾' }, { name: 'Liechtenstein', code: '+423', flag: '🇱🇮' }, { name: 'Lithuania', code: '+370', flag: '🇱🇹' }, { name: 'Luxembourg', code: '+352', flag: '🇱🇺' }, { name: 'Macau', code: '+853', flag: '🇲🇴' }, { name: 'Macedonia', code: '+389', flag: '🇲🇰' }, { name: 'Madagascar', code: '+261', flag: '🇲🇬' }, { name: 'Malawi', code: '+265', flag: '🇲🇼' }, { name: 'Maldives', code: '+960', flag: '🇲🇻' }, { name: 'Mali', code: '+223', flag: '🇲🇱' }, { name: 'Malta', code: '+356', flag: '🇲🇹' }, { name: 'Marshall Islands', code: '+692', flag: '🇲🇭' }, { name: 'Mauritania', code: '+222', flag: '🇲🇷' }, { name: 'Mauritius', code: '+230', flag: '🇲🇺' }, { name: 'Mayotte', code: '+262', flag: '🇾🇹' }, { name: 'Mexico', code: '+52', flag: '🇲🇽' }, { name: 'Micronesia', code: '+691', flag: '🇫🇲' }, { name: 'Moldova', code: '+373', flag: '🇲🇩' }, { name: 'Monaco', code: '+377', flag: '🇲🇨' }, { name: 'Mongolia', code: '+976', flag: '🇲🇳' }, { name: 'Montenegro', code: '+382', flag: '🇲🇪' }, { name: 'Montserrat', code: '+1664', flag: '🇲🇸' }, { name: 'Morocco', code: '+212', flag: '🇲🇦' }, { name: 'Mozambique', code: '+258', flag: '🇲🇿' }, { name: 'Myanmar', code: '+95', flag: '🇲🇲' }, { name: 'Namibia', code: '+264', flag: '🇳🇦' }, { name: 'Nauru', code: '+674', flag: '🇳🇷' }, { name: 'Nepal', code: '+977', flag: '🇳🇵' }, { name: 'Netherlands', code: '+31', flag: '🇳🇱' }, { name: 'New Caledonia', code: '+687', flag: '🇳🇨' }, { name: 'New Zealand', code: '+64', flag: '🇳🇿' }, { name: 'Nicaragua', code: '+505', flag: '🇳🇮' }, { name: 'Niger', code: '+227', flag: '🇳🇪' }, { name: 'Nigeria', code: '+234', flag: '🇳🇬' }, { name: 'Niue', code: '+683', flag: '🇳🇺' }, { name: 'North Korea', code: '+850', flag: '🇰🇵' }, { name: 'Northern Mariana Islands', code: '+1670', flag: '🇲🇵' }, { name: 'Norway', code: '+47', flag: '🇳🇴' }, { name: 'Oman', code: '+968', flag: '🇴🇲' }, { name: 'Pakistan', code: '+92', flag: '🇵🇰' }, { name: 'Palau', code: '+680', flag: '🇵🇼' }, { name: 'Palestine', code: '+970', flag: '🇵🇸' }, { name: 'Panama', code: '+507', flag: '🇵🇦' }, { name: 'Papua New Guinea', code: '+675', flag: '🇵🇬' }, { name: 'Paraguay', code: '+595', flag: '🇵🇾' }, { name: 'Peru', code: '+51', flag: '🇵🇪' }, { name: 'Philippines', code: '+63', flag: '🇵🇭' }, { name: 'Pitcairn', code: '+64', flag: '🇵🇳' }, { name: 'Poland', code: '+48', flag: '🇵🇱' }, { name: 'Portugal', code: '+351', flag: '🇵🇹' }, { name: 'Puerto Rico', code: '+1', flag: '🇵🇷' }, { name: 'Qatar', code: '+974', flag: '🇶🇦' }, { name: 'Republic of the Congo', code: '+242', flag: '🇨🇬' }, { name: 'Reunion', code: '+262', flag: '🇷🇪' }, { name: 'Romania', code: '+40', flag: '🇷🇴' }, { name: 'Russia', code: '+7', flag: '🇷🇺' }, { name: 'Rwanda', code: '+250', flag: '🇷🇼' }, { name: 'Saint Barthelemy', code: '+590', flag: '🇧🇱' }, { name: 'Saint Helena', code: '+290', flag: '🇸🇭' }, { name: 'Saint Kitts and Nevis', code: '+1869', flag: '🇰🇳' }, { name: 'Saint Lucia', code: '+1758', flag: '🇱🇨' }, { name: 'Saint Martin', code: '+590', flag: '🇲🇫' }, { name: 'Saint Pierre and Miquelon', code: '+508', flag: '🇵🇲' }, { name: 'Saint Vincent and the Grenadines', code: '+1784', flag: '🇻🇨' }, { name: 'Samoa', code: '+685', flag: '🇼🇸' }, { name: 'San Marino', code: '+378', flag: '🇸🇲' }, { name: 'Sao Tome and Principe', code: '+239', flag: '🇸🇹' }, { name: 'Saudi Arabia', code: '+966', flag: '🇸🇦' }, { name: 'Senegal', code: '+221', flag: '🇸🇳' }, { name: 'Serbia', code: '+381', flag: '🇷🇸' }, { name: 'Seychelles', code: '+248', flag: '🇸🇨' }, { name: 'Sierra Leone', code: '+232', flag: '🇸🇱' }, { name: 'Sint Maarten', code: '+1721', flag: '🇸🇽' }, { name: 'Slovakia', code: '+421', flag: '🇸🇰' }, { name: 'Slovenia', code: '+386', flag: '🇸🇮' }, { name: 'Solomon Islands', code: '+677', flag: '🇸🇧' }, { name: 'Somalia', code: '+252', flag: '🇸🇴' }, { name: 'South Africa', code: '+27', flag: '🇿🇦' }, { name: 'South Korea', code: '+82', flag: '🇰🇷' }, { name: 'South Sudan', code: '+211', flag: '🇸🇸' }, { name: 'Spain', code: '+34', flag: '🇪🇸' }, { name: 'Sri Lanka', code: '+94', flag: '🇱🇰' }, { name: 'Sudan', code: '+249', flag: '🇸🇩' }, { name: 'Suriname', code: '+597', flag: '🇸🇷' }, { name: 'Svalbard and Jan Mayen', code: '+47', flag: '🇸🇯' }, { name: 'Swaziland', code: '+268', flag: '🇸🇿' }, { name: 'Sweden', code: '+46', flag: '🇸🇪' }, { name: 'Switzerland', code: '+41', flag: '🇨🇭' }, { name: 'Syria', code: '+963', flag: '🇸🇾' }, { name: 'Taiwan', code: '+886', flag: '🇹🇼' }, { name: 'Tajikistan', code: '+992', flag: '🇹🇯' }, { name: 'Tanzania', code: '+255', flag: '🇹🇿' }, { name: 'Thailand', code: '+66', flag: '🇹🇭' }, { name: 'Togo', code: '+228', flag: '🇹🇬' }, { name: 'Tokelau', code: '+690', flag: '🇹🇰' }, { name: 'Tonga', code: '+676', flag: '🇹🇴' }, { name: 'Trinidad and Tobago', code: '+1868', flag: '🇹🇹' }, { name: 'Tunisia', code: '+216', flag: '🇹🇳' }, { name: 'Turkey', code: '+90', flag: '🇹🇷' }, { name: 'Turkmenistan', code: '+993', flag: '🇹🇲' }, { name: 'Turks and Caicos Islands', code: '+1649', flag: '🇹🇨' }, { name: 'Tuvalu', code: '+688', flag: '🇹🇻' }, { name: 'U.S. Virgin Islands', code: '+1340', flag: '🇻🇮' }, { name: 'Uganda', code: '+256', flag: '🇺🇬' }, { name: 'Ukraine', code: '+380', flag: '🇺🇦' }, { name: 'United Arab Emirates', code: '+971', flag: '🇦🇪' }, { name: 'United Kingdom', code: '+44', flag: '🇬🇧' }, { name: 'United States', code: '+1', flag: '🇺🇸' }, { name: 'Uruguay', code: '+598', flag: '🇺🇾' }, { name: 'Uzbekistan', code: '+998', flag: '🇺🇿' }, { name: 'Vanuatu', code: '+678', flag: '🇻🇺' }, { name: 'Vatican', code: '+379', flag: '🇻🇦' }, { name: 'Venezuela', code: '+58', flag: '🇻🇪' }, { name: 'Vietnam', code: '+84', flag: '🇻🇳' }, { name: 'Wallis and Futuna', code: '+681', flag: '🇼🇫' }, { name: 'Western Sahara', code: '+212', flag: '🇪🇭' }, { name: 'Yemen', code: '+967', flag: '🇾🇪' }, { name: 'Zambia', code: '+260', flag: '🇿🇲' }, { name: 'Zimbabwe', code: '+263', flag: '🇿🇼' }, ];

const PhoneField: React.FC<{
    label: string;
    name: string;
    fullWidth?: boolean;
    value: string;
    onChange: (value: string) => void;
    highlight?: boolean;
    isPrintView?: boolean;
}> = ({ label, name, fullWidth = false, value, onChange, highlight = false, isPrintView = false }) => {
    if (isPrintView) {
         return (
             <div className={`${fullWidth ? 'md:col-span-2 lg:col-span-3' : ''} print:mb-1`}>
                <label className="block text-sm font-semibold text-slate-700 mb-1 print:mb-0 print:text-xs print:text-slate-500">{label}</label>
                <div className="text-sm text-slate-900 p-2 min-h-[38px] bg-slate-50 rounded-md print:p-0 print:min-h-0 print:bg-transparent print:border-b print:border-slate-300 print:rounded-none print:pb-1 print:leading-snug">{value || '-'}</div>
            </div>
        );
    }
    const [isOpen, setIsOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const dropdownRef = useRef<HTMLDivElement>(null);
    
    const parts = value.split('-');
    const countryCode = parts.length > 1 ? parts[0] : '+60';
    const phoneNumber = parts.length > 1 ? parts.slice(1).join('-') : parts[0] || '';
    
    const selectedCountry = countries.find(c => c.code === countryCode) || countries[0];

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    const filteredCountries = countries.filter(country =>
        country.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        country.code.includes(searchTerm)
    );

    const handleSelectCountry = (country: typeof countries[0]) => {
        onChange(`${country.code}-${phoneNumber}`);
        setIsOpen(false);
        setSearchTerm('');
    };
    
    const handlePhoneChange = (e: ChangeEvent<HTMLInputElement>) => {
        onChange(`${countryCode}-${e.target.value}`);
    };

    const autoHighlight = !isPrintView && !phoneNumber.trim();
    return (
        <div className={fullWidth ? 'md:col-span-2 lg:col-span-3' : ''}>
            <label htmlFor={name} className="block text-sm font-semibold text-slate-700 mb-2">{label}</label>
            <div className={`flex items-center rounded-lg ${(highlight || autoHighlight) ? 'animate-pulse-highlight' : ''}`}>
                <div className="relative" ref={dropdownRef}>
                    <input type="hidden" name={`${name}-code`} value={selectedCountry.code} />
                    <button
                        type="button"
                        onClick={() => setIsOpen(!isOpen)}
                        className="flex-shrink-0 z-10 inline-flex items-center py-2.5 px-4 text-sm font-medium text-center text-slate-800 bg-slate-100 border border-slate-300 rounded-l-lg hover:bg-slate-200 focus:outline-none focus:ring-2 focus:ring-brand-primary/80 transition-colors"
                    >
                        <span className="mr-2 text-base leading-none">{selectedCountry.flag}</span>
                        <span>{selectedCountry.code}</span>
                        <svg className="w-2.5 h-2.5 ml-2.5" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 10 6">
                            <path stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="m1 1 4 4 4-4"/>
                        </svg>
                    </button>
                    {isOpen && (
                        <div className="absolute z-20 mt-1 w-72 bg-white rounded-lg shadow-lg max-h-60 overflow-y-auto ring-1 ring-black ring-opacity-5 focus:outline-none">
                            <div className="p-2">
                                <input
                                    type="text"
                                    placeholder="Search country..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    className="w-full px-3 py-2 text-sm border border-slate-300 rounded-md focus:outline-none focus:ring-1 focus:ring-brand-primary"
                                />
                            </div>
                            <ul role="listbox">
                                {filteredCountries.map(country => (
                                    <li key={country.code} onClick={() => handleSelectCountry(country)} className="text-slate-900 cursor-pointer select-none relative py-2 pl-3 pr-9 hover:bg-slate-100" role="option">
                                        <div className="flex items-center">
                                            <span className="mr-3 text-lg">{country.flag}</span>
                                            <span className="font-normal block truncate">{country.name} ({country.code})</span>
                                        </div>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}
                </div>
                <input
                    id={name}
                    name={name}
                    type="tel"
                    placeholder="12-345 6789"
                    value={phoneNumber}
                    onChange={handlePhoneChange}
                    className="relative p-2.5 w-full text-sm text-slate-900 bg-white rounded-r-lg border border-l-0 border-slate-300 focus:outline-none focus:ring-2 focus:ring-brand-primary/80 focus:border-brand-primary focus:z-10 placeholder-slate-400 transition-colors"
                />
            </div>
        </div>
    );
};

type UploadStatus = 'uploading' | 'success' | 'error';
interface UploadProgress {
    status: UploadStatus;
    progress: number;
}

// File Upload Component
const FileUpload: React.FC<{ 
    title: string; 
    maxFiles: number; 
    attachments?: Attachment[];
    pendingFiles?: File[];
    uploadProgress: Record<string, UploadProgress>;
    onFileSelect: (files: File[]) => void;
    onRemovePendingFile: (fileName: string) => void;
    isPrintView?: boolean; 
    children?: React.ReactNode;
}> = ({ title, maxFiles, attachments, pendingFiles, uploadProgress, onFileSelect, onRemovePendingFile, isPrintView = false, children }) => {
    // Ensure arrays are always defined to avoid length errors when API returns null/undefined
    const safeAttachments = attachments ?? [];
    const safePendingFiles = pendingFiles ?? [];
    const [previews, setPreviews] = useState<Record<string, string>>({});
    const [downloading, setDownloading] = useState<Record<string, boolean>>({});
    const [attachmentPreviews, setAttachmentPreviews] = useState<Record<string, string>>({});

    const uploadInputRef = useRef<HTMLInputElement>(null);
    const cameraInputRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        const newPreviews: Record<string, string> = {};
        let shouldUpdatePreviews = false;

        safePendingFiles.forEach(file => {
            if (!previews[file.name] && file.type.startsWith('image/')) {
                newPreviews[file.name] = URL.createObjectURL(file);
                shouldUpdatePreviews = true;
            }
        });

        if (shouldUpdatePreviews) {
            setPreviews(prev => ({ ...prev, ...newPreviews }));
        }

        const pendingFileNames = new Set(safePendingFiles.map(f => f.name));
        Object.keys(previews).forEach(fileName => {
            if (!pendingFileNames.has(fileName)) {
                URL.revokeObjectURL(previews[fileName]);
            }
        });

        return () => {
             Object.values(previews).forEach(URL.revokeObjectURL);
        }
    }, [safePendingFiles, previews]);

    useEffect(() => {
        const isImageFile = (fileName: string): boolean => /\.(jpg|jpeg|png|gif|webp|bmp|tiff)$/i.test(fileName);

        const fetchPreviews = async () => {
            const imageAttachments = safeAttachments.filter(
                att => isImageFile(att.attachment_name) && !attachmentPreviews[att.id]
            );

            if (imageAttachments.length === 0) return;

            for (const attachment of imageAttachments) {
                const apiUrl = `${API_URL}/resident/attachment?url=${encodeURIComponent(attachment.attachment_url_s3)}`;

                console.log('--- Download API Call (Preview) ---');
                console.log('API URL:', apiUrl);
                
                try {
                    const response = await fetch(apiUrl, {
                        method: 'GET',
                        headers: {
                            'x-api-key': API_KEY_EN,
                        },
                    });
                    
                    const responseText = await response.text();
                    console.log('Response:', responseText);

                    if (!response.ok) {
                        console.error(`Failed to get preview link for ${attachment.attachment_name}. Status: ${response.status}`);
                        continue;
                    }

                    let previewUrl: string | null = null;
                    try {
                        const parsedResponse = JSON.parse(responseText);
                        if (typeof parsedResponse === 'string') {
                            previewUrl = parsedResponse;
                        } else if (typeof parsedResponse === 'object' && parsedResponse !== null) {
                            previewUrl = parsedResponse.url || parsedResponse.downloadUrl;
                        }
                    } catch (e) {
                        if (responseText.startsWith('http')) {
                            previewUrl = responseText;
                        }
                    }

                    if (previewUrl) {
                        setAttachmentPreviews(prev => ({ ...prev, [attachment.id]: previewUrl }));
                    }
                } catch (error) {
                    console.error(`Error fetching preview for ${attachment.attachment_name}`, error);
                }
            }
        };

        fetchPreviews();
    }, [safeAttachments, attachmentPreviews]);

    const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
        const selectedFiles = event.target.files;
        if (!selectedFiles) return;
        onFileSelect(Array.from(selectedFiles));
        event.target.value = '';
    };

    const formatFileSize = (bytes: number) => {
        if (bytes === 0) return '0 Bytes';
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
    };

    const handleDownload = async (attachment: Attachment) => {
        setDownloading(prev => ({ ...prev, [attachment.id]: true }));
        const apiUrl = `${API_URL}/resident/attachment?url=${encodeURIComponent(attachment.attachment_url_s3)}`;
        
        console.log('--- Download API Call (Download) ---');
        console.log('API URL:', apiUrl);

        try {
            const response = await fetch(apiUrl, {
                method: 'GET',
                headers: {
                    'x-api-key': API_KEY_EN,
                },
            });

            const responseText = await response.text();
            console.log('Response:', responseText);

            if (!response.ok) {
                throw new Error(`Failed to get download link. Status: ${response.status}`);
            }
            
            let downloadUrl: string | null = null;
            try {
                const parsedResponse = JSON.parse(responseText);
                if (typeof parsedResponse === 'string') {
                    downloadUrl = parsedResponse;
                } else if (typeof parsedResponse === 'object' && parsedResponse !== null) {
                    downloadUrl = parsedResponse.url || parsedResponse.downloadUrl;
                }
            } catch (e) {
                if (responseText.startsWith('http')) {
                    downloadUrl = responseText;
                }
            }

            if (downloadUrl) {
                window.open(downloadUrl, '_blank');
            } else {
                throw new Error('Invalid response from download API: could not find a valid URL.');
            }
        } catch (error) {
            console.error("Download failed", error);
            alert(`Could not download file: ${attachment.attachment_name}`);
        } finally {
            setDownloading(prev => ({ ...prev, [attachment.id]: false }));
        }
    };
    
    const canAddMoreFiles = (safeAttachments.length + safePendingFiles.length) < maxFiles;

    return (
        <div className="py-8 border-b border-slate-200 print:border-none print:py-4 print:break-inside-avoid">
            <h3 className="text-xl font-semibold text-slate-800 mb-4 print:mb-2 print:text-lg">{title}</h3>
            {children}
            <div className="bg-slate-50/70 p-6 rounded-xl border border-slate-200 print:p-0 print:border-none print:bg-transparent">
                {!isPrintView && (
                    <div className="flex flex-col items-center justify-center p-6 border-2 border-dashed border-slate-300 rounded-lg text-center bg-white">
                        <svg className="w-12 h-12 text-slate-400 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"></path></svg>
                        <p className="mb-2 text-sm text-slate-600"><span className="font-semibold">Click to upload</span> or drag and drop</p>
                        <p className="text-xs text-slate-500 mb-4">PNG, JPG, PDF, etc. (MAX. {maxFiles} files)</p>
                        <div className="flex flex-col sm:flex-row items-center justify-center space-y-3 sm:space-y-0 sm:space-x-4">
                            <input type="file" ref={uploadInputRef} onChange={handleFileChange} accept="image/*,application/pdf" multiple className="hidden" disabled={!canAddMoreFiles} />
                            <input type="file" ref={cameraInputRef} onChange={handleFileChange} accept="image/*" capture="environment" className="hidden" disabled={!canAddMoreFiles} />
                            
                            <button type="button" onClick={() => uploadInputRef.current?.click()} disabled={!canAddMoreFiles} className="w-full sm:w-auto px-5 py-2.5 bg-white text-slate-700 font-semibold border border-slate-300 rounded-lg shadow-sm hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-primary transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
                                Upload File
                            </button>
                            <button type="button" onClick={() => cameraInputRef.current?.click()} disabled={!canAddMoreFiles} className="w-full sm:w-auto flex items-center justify-center px-5 py-2.5 bg-brand-primary text-white font-semibold rounded-lg shadow-md hover:bg-brand-primary/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-primary transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
                                <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z"></path><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 13a3 3 0 11-6 0 3 3 0 016 0z"></path></svg>
                                Use Camera
                            </button>
                        </div>
                    </div>
                )}

                {(safeAttachments.length > 0 || safePendingFiles.length > 0) && (
                    <div className="mt-6 print:mt-0">
                        <h4 className="font-semibold text-slate-700 mb-3">{`Uploaded Files (${safeAttachments.length + safePendingFiles.length}/${maxFiles})`}</h4>
                        <ul className="space-y-3">
                             {safeAttachments.map((file) => {
                                const isImage = /\.(jpg|jpeg|png|gif|webp|bmp|tiff)$/i.test(file.attachment_name);
                                return (
                                <li key={file.id} className="flex items-center justify-between bg-white p-3 rounded-lg border border-slate-200 shadow-sm print:shadow-none print:border-slate-300">
                                    <div className="flex items-center overflow-hidden">
                                        <div className="w-12 h-12 rounded-md bg-slate-100 flex items-center justify-center mr-4 flex-shrink-0">
                                            {isImage && attachmentPreviews[file.id] ? (
                                                <img src={attachmentPreviews[file.id]} alt={file.attachment_name} className="w-full h-full object-cover rounded-md" />
                                            ) : (
                                                <svg className="w-6 h-6 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0011.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z"></path></svg>
                                            )}
                                        </div>
                                        <div className="overflow-hidden">
                                            <button type="button" onClick={() => handleDownload(file)} disabled={downloading[file.id]} className="text-sm font-medium text-slate-800 truncate hover:text-brand-primary text-left disabled:text-slate-400 disabled:cursor-wait" title={file.attachment_name}>{file.attachment_name}</button>
                                            <p className="text-xs text-slate-500">Previously uploaded</p>
                                        </div>
                                    </div>
                                    {downloading[file.id] && <svg className="animate-spin h-5 w-5 text-brand-primary" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path></svg>}
                                </li>
                                );
                             })}
                            {safePendingFiles.map((file) => (
                                <li key={file.name} className="flex items-center justify-between bg-white p-3 rounded-lg border border-slate-200 shadow-sm">
                                    <div className="flex items-center overflow-hidden w-full">
                                        {previews[file.name] ? (
                                            <img src={previews[file.name]} alt="preview" className="w-12 h-12 rounded-md object-cover mr-4 flex-shrink-0" />
                                        ) : (
                                            <div className="w-12 h-12 rounded-md bg-slate-100 flex items-center justify-center mr-4 flex-shrink-0">
                                                <svg className="w-6 h-6 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0011.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z"></path></svg>
                                            </div>
                                        )}
                                        <div className="overflow-hidden flex-grow">
                                            <p className="text-sm font-medium text-slate-800 truncate" title={file.name}>{file.name}</p>
                                            <p className="text-xs text-slate-500">{formatFileSize(file.size)}</p>
                                             {uploadProgress[file.name] && (
                                                <div className="mt-1.5">
                                                    <div className="flex justify-between items-center text-xs text-slate-500 mb-0.5">
                                                        <span className="capitalize">{uploadProgress[file.name].status}</span>
                                                        <span>{uploadProgress[file.name].progress}%</span>
                                                    </div>
                                                    <div className="w-full bg-slate-200 rounded-full h-1">
                                                        <div 
                                                            className={`h-1 rounded-full ${
                                                                uploadProgress[file.name].status === 'error' ? 'bg-red-500' :
                                                                uploadProgress[file.name].status === 'success' ? 'bg-green-500' :
                                                                'bg-brand-primary'
                                                            }`} 
                                                            style={{ width: `${uploadProgress[file.name].progress}%`, transition: 'width 0.2s ease' }}
                                                        ></div>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                    {!isPrintView && (
                                      <button onClick={() => onRemovePendingFile(file.name)} className="ml-4 p-1.5 rounded-full text-slate-500 hover:bg-red-100 hover:text-red-600 focus:outline-none focus:ring-2 focus:ring-red-500 transition-colors flex-shrink-0">
                                          <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg"><path fillRule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd"></path></svg>
                                      </button>
                                    )}
                                </li>
                            ))}
                        </ul>
                    </div>
                )}
            </div>
        </div>
    );
};

const hospitalsInMalaysia = [
  'Hospital Kuala Lumpur (HKL)',
  'Sunway Medical Centre',
  'Gleneagles Kuala Lumpur',
  'Pantai Hospital Kuala Lumpur',
  'Prince Court Medical Centre',
  'Subang Jaya Medical Centre (SJMC)',
  'Tung Shin Hospital',
  'University Malaya Medical Centre (UMMC)',
  'Hospital Selayang',
  'Hospital Putrajaya',
  'KPJ Damansara Specialist Hospital',
  'Assunta Hospital',
  'Columbia Asia Hospital - Petaling Jaya',
  'Thomson Hospital Kota Damansara',
  'Island Hospital, Penang',
  'LohGuanLye Specialists Centre, Penang',
  'Hospital Sultanah Aminah, Johor Bahru',
  'Regency Specialist Hospital, Johor',
  'Mahkota Medical Centre, Melaka',
  'Hospital Queen Elizabeth, Kota Kinabalu',
];

// Reusable combobox component for hospital selection
const ComboBoxField: React.FC<{
    label: string;
    name: string;
    options: string[];
    placeholder?: string;
    fullWidth?: boolean;
    value: string;
    onChange: (value: string) => void;
    highlight?: boolean;
    isPrintView?: boolean;
}> = ({ label, name, options, placeholder = '', fullWidth = false, value, onChange, highlight = false, isPrintView = false }) => {
    if (isPrintView) {
        return (
             <div className={`${fullWidth ? 'md:col-span-2 lg:col-span-3' : ''} print:mb-1`}>
                <label className="block text-sm font-semibold text-slate-700 mb-1 print:mb-0 print:text-xs print:text-slate-500">{label}</label>
                <div className="text-sm text-slate-900 p-2 min-h-[38px] bg-slate-50 rounded-md print:p-0 print:min-h-0 print:bg-transparent print:border-b print:border-slate-300 print:rounded-none print:pb-1 print:leading-snug">{value || '-'}</div>
            </div>
        );
    }
    const [isOpen, setIsOpen] = useState(false);
    const wrapperRef = useRef<HTMLDivElement>(null);

    const filteredOptions = options.filter(option =>
        option.toLowerCase().includes(value.toLowerCase())
    );

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [wrapperRef]);

    const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
        onChange(e.target.value);
        if (!isOpen) setIsOpen(true);
    };

    const handleOptionClick = (option: string) => {
        onChange(option);
        setIsOpen(false);
    };

    return (
        <div className={fullWidth ? 'md:col-span-2 lg:col-span-3' : ''} ref={wrapperRef}>
            <label htmlFor={name} className="block text-sm font-semibold text-slate-700 mb-2">{label}</label>
            <div className="relative">
                <input
                    id={name}
                    name={name}
                    type="text"
                    placeholder={placeholder}
                    value={value}
                    onChange={handleInputChange}
                    onFocus={() => setIsOpen(true)}
                    className={`block w-full px-4 py-2 bg-white text-slate-900 border border-slate-300 rounded-lg shadow-sm placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-brand-primary/80 focus:border-brand-primary hover:border-slate-400 transition-all duration-200 ${highlight ? 'animate-pulse-highlight' : ''}`}
                    autoComplete="off"
                />
                <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                     <svg className="h-5 w-5 text-slate-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7 7" />
                    </svg>
                </div>
                {isOpen && (
                    <div className="absolute z-10 mt-1 w-full bg-white rounded-md shadow-lg max-h-60 overflow-y-auto ring-1 ring-black ring-opacity-5 focus:outline-none">
                        <ul role="listbox">
                            {filteredOptions.length > 0 ? filteredOptions.map((option, index) => (
                                <li
                                    key={index}
                                    onClick={() => handleOptionClick(option)}
                                    className="text-slate-900 cursor-pointer select-none relative py-2 pl-4 pr-9 hover:bg-brand-primary-light"
                                    role="option"
                                >
                                    <span className="font-normal block truncate">{option}</span>
                                </li>
                            )) : (
                                <li className="text-slate-500 select-none relative py-2 px-4">No results found</li>
                            )}
                        </ul>
                    </div>
                )}
            </div>
        </div>
    );
};

// Reusable component for modern signature pads
const SignaturePad: React.FC<{ 
    label: string; 
    signature: string | null;
    onSign: (dataUrl: string | null) => void;
    highlight?: boolean;
    isPrintView?: boolean;
    readOnly?: boolean;
}> = ({ label, signature, onSign, highlight = false, isPrintView = false, readOnly = false }) => {
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const [isDrawing, setIsDrawing] = useState(false);
    const [isHovered, setIsHovered] = useState(false);

    if (isPrintView) {
         return (
            <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2 print:mb-1 print:text-xs print:text-slate-500">{label}</label>
                <div className="relative w-full h-32 bg-slate-50 border border-slate-300 rounded-lg flex items-center justify-center print:h-24 print:bg-transparent print:rounded-none">
                    {signature ? (
                        <img src={signature} alt={label} className="max-w-full max-h-full object-contain" />
                    ) : (
                        <span className="text-sm text-slate-500 font-medium">
                            [ Not Signed ]
                        </span>
                    )}
                </div>
            </div>
        );
    }

    const getCoords = (event: MouseEvent | TouchEvent) => {
        const canvas = canvasRef.current;
        if (!canvas) return { x: 0, y: 0 };
        const rect = canvas.getBoundingClientRect();
        if (event instanceof MouseEvent) return { x: event.clientX - rect.left, y: event.clientY - rect.top };
        if (event.touches?.[0]) return { x: event.touches[0].clientX - rect.left, y: event.touches[0].clientY - rect.top };
        return { x: 0, y: 0 };
    };

    const startDrawing = (event: React.MouseEvent<HTMLCanvasElement> | React.TouchEvent<HTMLCanvasElement>) => {
        if (readOnly) return;
        event.preventDefault();
        const canvas = canvasRef.current;
        const ctx = canvas?.getContext('2d');
        if (!ctx) return;
        const { x, y } = getCoords(event.nativeEvent);
        ctx.beginPath();
        ctx.moveTo(x, y);
        setIsDrawing(true);
    };

    const draw = (event: React.MouseEvent<HTMLCanvasElement> | React.TouchEvent<HTMLCanvasElement>) => {
        if (readOnly || !isDrawing) return;
        event.preventDefault();
        const { x, y } = getCoords(event.nativeEvent);
        const canvas = canvasRef.current;
        const ctx = canvas?.getContext('2d');
        if (!ctx) return;
        ctx.lineTo(x, y);
        ctx.stroke();
    };

    const stopDrawing = () => {
        if (readOnly || !isDrawing) return;
        setIsDrawing(false);
        const canvas = canvasRef.current;
        if (canvas) {
            onSign(canvas.toDataURL('image/jpeg'));
        }
    };
    
    const clearCanvas = () => {
        if (readOnly) return;
        const canvas = canvasRef.current;
        const ctx = canvas?.getContext('2d');
        if (ctx && canvas) {
            const rect = canvas.getBoundingClientRect();
            ctx.fillStyle = 'white';
            ctx.fillRect(0, 0, rect.width, rect.height);
            onSign(null);
        }
    };

    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        const ctx = canvas.getContext('2d');
        if (!ctx) return;
        const dpr = window.devicePixelRatio || 1;
        const rect = canvas.getBoundingClientRect();
        canvas.width = rect.width * dpr;
        canvas.height = rect.height * dpr;
        ctx.scale(dpr, dpr);
        ctx.strokeStyle = '#374151';
        ctx.lineWidth = 2.5;
        ctx.lineCap = 'round';
        ctx.lineJoin = 'round';

        ctx.fillStyle = 'white';
        ctx.fillRect(0, 0, rect.width, rect.height);

        if (signature) {
            const img = new Image();
            img.crossOrigin = 'anonymous';
            img.onload = () => {
                ctx.fillStyle = 'white';
                ctx.fillRect(0, 0, rect.width, rect.height);
                ctx.drawImage(img, 0, 0, rect.width, rect.height);
            }
            img.src = signature;
        }
    }, [signature]);

    return (
        <div>
            <label className="block text-sm font-semibold text-slate-700 mb-2">{label}</label>
            <div 
                className={`relative w-full h-48 bg-white border-2 border-dashed border-slate-300 rounded-lg transition-colors duration-200 ${highlight ? 'animate-pulse-highlight' : ''} ${readOnly ? 'cursor-not-allowed bg-slate-50' : 'cursor-crosshair hover:border-brand-primary/80'}`}
                onMouseEnter={() => setIsHovered(true)}
                onMouseLeave={() => setIsHovered(false)}
            >
                <canvas
                    ref={canvasRef}
                    className="w-full h-full rounded-lg"
                    onMouseDown={startDrawing} onMouseMove={draw} onMouseUp={stopDrawing} onMouseLeave={stopDrawing}
                    onTouchStart={startDrawing} onTouchMove={draw} onTouchEnd={stopDrawing}
                />
                {!signature && !readOnly && (
                    <span className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 text-slate-400 font-medium pointer-events-none">
                        Sign here
                    </span>
                )}
                {readOnly && signature && (
                    <div className="absolute top-2 right-2 flex items-center bg-green-100 text-green-700 text-xs font-bold px-2 py-1 rounded-full z-10 pointer-events-none">
                        <svg className="w-3.5 h-3.5 mr-1" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                        Signed
                    </div>
                )}
                {!readOnly && signature && isHovered && (
                     <button
                        type="button"
                        onClick={clearCanvas}
                        className="absolute top-2 right-2 p-1.5 bg-white/80 backdrop-blur-sm rounded-full text-slate-500 hover:bg-red-100 hover:text-red-600 focus:outline-none focus:ring-2 focus:ring-red-500 transition-colors z-10"
                        aria-label="Clear signature"
                    >
                        <svg className="w-4 h-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                           <path fillRule="evenodd" d="M8.75 1A2.75 2.75 0 006 3.75v.443c-.795.077-1.58.22-2.365.468a.75.75 0 10.23 1.482l.149-.046a.75.75 0 01.701.043l2.25 1.5a.75.75 0 01.043.701l-.046.149a.75.75 0 101.482.23l.468-2.365v-.443A2.75 2.75 0 008.75 1zM3.75 8a.75.75 0 01.75.75v6.5a.75.75 0 01-1.5 0v-6.5A.75.75 0 013.75 8zM8 8a.75.75 0 01.75.75v6.5a.75.75 0 01-1.5 0v-6.5A.75.75 0 018 8zM12.25 8a.75.75 0 01.75.75v6.5a.75.75 0 01-1.5 0v-6.5a.75.75 0 01.75-.75zM16 8a.75.75 0 01.75.75v6.5a.75.75 0 01-1.5 0v-6.5A.75.75 0 0116 8z" clipRule="evenodd" />
                        </svg>
                    </button>
                )}
            </div>
        </div>
    );
};


// Helper function to extract DOB from IC
const getDobFromIc = (icValue: string): string | null => {
    const cleanedIc = icValue.replace(/[^0-9]/g, '');

    if (cleanedIc.length >= 6) {
        const dobPart = cleanedIc.substring(0, 6);
        let year = parseInt(dobPart.substring(0, 2), 10);
        const month = dobPart.substring(2, 4);
        const day = dobPart.substring(4, 6);

        const currentYearLastTwoDigits = new Date().getFullYear() % 100;
        year = year <= currentYearLastTwoDigits ? 2000 + year : 1900 + year;

        const monthNum = parseInt(month, 10);
        const dayNum = parseInt(day, 10);

        // Check for a valid date
        const tempDate = new Date(year, monthNum - 1, dayNum);
        if (
            tempDate.getFullYear() === year &&
            tempDate.getMonth() === monthNum - 1 &&
            tempDate.getDate() === dayNum
        ) {
            return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
        }
    }
    return null;
};

const isFieldEmpty = (value: any): boolean => {
    if (value === null || value === undefined) return true;
    if (typeof value === 'string' && value.trim() === '') return true;
    if (typeof value === 'number' && isNaN(value)) return true;
    if (Array.isArray(value) && value.length === 0) return true;
    return false;
};

function dataURLtoFile(dataurl: string, filename: string): File {
    const arr = dataurl.split(',');
    const mimeMatch = arr[0].match(/:(.*?);/);
    if (!mimeMatch) {
        throw new Error('Invalid data URL');
    }
    const mime = mimeMatch[1];
    const bstr = atob(arr[1]);
    let n = bstr.length;
    const u8arr = new Uint8Array(n);
    while (n--) {
        u8arr[n] = bstr.charCodeAt(n);
    }
    return new File([u8arr], filename, { type: mime });
}


const AdmissionPage: React.FC<AdmissionPageProps> = ({ data, onUpdate, isSaving, onContinue, isClientView, isPrintView = false, onError }) => {
    const formatDate = (isoString: string | null | undefined) => {
        if (!isoString) return '';
        const date = new Date(isoString);
        if (isNaN(date.getTime())) {
            return '';
        }
        return date.toISOString().split('T')[0];
    };
    
    // Admission Info
    const [admissionDate, setAdmissionDate] = useState(formatDate(data.date_of_admission));
    const [roomAssigned, setRoomAssigned] = useState(data.room_assigned || '');

    // Resident Info
    const [residentName, setResidentName] = useState(data.cr_name || '');
    const [residentIc, setResidentIc] = useState(data.cr_ic_passport || '');
    const [residentMobile, setResidentMobile] = useState(data.cr_mobile || '');
    const [residentAddress, setResidentAddress] = useState(data.cr_address || '');
    const [residentHeightCm, setResidentHeightCm] = useState(data.cr_height || '');
    const [residentWeight, setResidentWeight] = useState(data.cr_weight || '');
    const [residentAmbulatory, setResidentAmbulatory] = useState(data.cr_ambulatory === 1 ? 'Yes' : data.cr_ambulatory === 0 ? 'No' : '');
    const [residentGender, setResidentGender] = useState(data.cr_gender === 'male' ? 'Male' : data.cr_gender === 'female' ? 'Female' : '');
    const [residentLanguages, setResidentLanguages] = useState<string[]>(data.cr_language?.split(',').map(l => l.trim()) || []);
    const [residentDob, setResidentDob] = useState(formatDate(data.cr_dob));
    const [residentAge, setResidentAge] = useState<number | string>(data.cr_age || '');
    const [residentEmail, setResidentEmail] = useState(data.cr_email || '');
    
    // Surety Info
    const [suretyName, setSuretyName] = useState(data.co_name || '');
    const [suretyIc, setSuretyIc] = useState(data.co_ic_passport || '');
    const [suretyMobile, setSuretyMobile] = useState(data.co_mobile || '');
    const [suretyAddress, setSuretyAddress] = useState(data.co_address || '');
    const [suretyGender, setSuretyGender] = useState(data.co_gender === 'male' ? 'Male' : data.co_gender === 'female' ? 'Female' : '');
    const [suretyDob, setSuretyDob] = useState(formatDate(data.co_dob));
    const [specialNotes, setSpecialNotes] = useState(data.co_special_notes || '');
    
    // Care Info
    const [replenishDiapers, setReplenishDiapers] = useState(data.diapers === 1 ? 'Yes' : data.diapers === 0 ? 'No' : '');
    const [replenishMedication, setReplenishMedication] = useState(data.medication === 1 ? 'Yes' : data.medication === 0 ? 'No' : '');
    const [replenishMilkPowder, setReplenishMilkPowder] = useState(data.milk_powder === 1 ? 'Yes' : data.milk_powder === 0 ? 'No' : '');
    
    // Follow-up
    const [followupHospital, setFollowupHospital] = useState(data.follow_up_hospital || '');
    const [doctorName, setDoctorName] = useState(data.follow_up_doctor_name || '');
    const [doctorContact, setDoctorContact] = useState(data.follow_up_doctor_contact || '');
    
    // Emergency
    const [emergencyHospital, setEmergencyHospital] = useState(data.emergency_hospital || '');
    const [emergencyInstructions, setEmergencyInstructions] = useState(data.emergency_special_instruction || '');
    const [emergencyContactName, setEmergencyContactName] = useState(data.emergency_name || '');
    const [emergencyContactMobile, setEmergencyContactMobile] = useState(data.emergency_mobile || '');
    const [emergencyContactRelationship, setEmergencyContactRelationship] = useState(data.emergency_relationship || '');
    
    // Confirmation signature (base64 for new)
    const [confirmationSignature, setConfirmationSignature] = useState<string | null>(null);
    // Confirmation signature (URL for display)
    const [displayConfirmationSignature, setDisplayConfirmationSignature] = useState<string | null>(null);

    // Attachments
    const [pendingFiles, setPendingFiles] = useState<File[]>([]);
    const [uploadProgress, setUploadProgress] = useState<Record<string, UploadProgress>>({});
    const [isUploading, setIsUploading] = useState(false);
    const [isUploadingSignatures, setIsUploadingSignatures] = useState(false);

    const reportError = useCallback((message: string) => {
        if (onError) {
            onError(message);
        } else {
            console.error(message);
        }
    }, [onError]);

    // Initial state for dirty check
    const [initialState, setInitialState] = useState(data);

    const isConfirmationReady = !isFieldEmpty(suretyName) && !isFieldEmpty(suretyIc);

    const decryptedCustomerId = useMemo(() => {
        if (!data.customer_id) return '';
        try {
            const encKey = "CareConcierge";
            const decrypted = CryptoJS.AES.decrypt(data.customer_id, encKey);
            return decrypted.toString(CryptoJS.enc.Utf8);
        } catch (error) {
            console.error("Failed to decrypt customer ID:", error);
            return 'Decryption Error';
        }
    }, [data.customer_id]);
    
    useEffect(() => {
        const fetchSignatureUrl = async (s3Url: string) => {
            const apiUrl = `${API_URL}/resident/attachment?url=${encodeURIComponent(s3Url)}`;
            try {
                const response = await fetch(apiUrl, {
                    method: 'GET',
                    headers: { 'x-api-key': API_KEY_EN },
                });
                if (!response.ok) return null;
                const responseText = await response.text();
                let url: string | null = null;
                try {
                    const parsed = JSON.parse(responseText);
                    url = typeof parsed === 'string' ? parsed : parsed.url || parsed.downloadUrl;
                } catch {
                    url = responseText.startsWith('http') ? responseText : null;
                }
                return url;
            } catch {
                return null;
            }
        };

        const confirmationSignData = Array.isArray(data.signatures)
            ? data.signatures.find(s => s.type === 'admission_co_type')
            : null;
        if (confirmationSignData?.signature_url_s3) {
            fetchSignatureUrl(confirmationSignData.signature_url_s3).then(url => {
                if (url) setDisplayConfirmationSignature(url);
            });
        } else {
            setDisplayConfirmationSignature(null);
        }

    }, [data.signatures]);


    useEffect(() => {
        // Always update the baseline for dirty checking when data prop changes.
        setInitialState(data);
    }, [data]);

    useEffect(() => {
        // Only populate form fields when the record ID changes, to preserve user input during refetches of the same record.
        setAdmissionDate(formatDate(data.date_of_admission));
        setRoomAssigned(data.room_assigned || '');
        setResidentName(data.cr_name || '');
        setResidentIc(data.cr_ic_passport || '');
        setResidentMobile(data.cr_mobile || '');
        setResidentAddress(data.cr_address || '');
        setResidentHeightCm(data.cr_height || '');
        setResidentWeight(data.cr_weight || '');
        setResidentAmbulatory(data.cr_ambulatory === 1 ? 'Yes' : data.cr_ambulatory === 0 ? 'No' : '');
        setResidentGender(data.cr_gender === 'male' ? 'Male' : data.cr_gender === 'female' ? 'Female' : '');
        setResidentLanguages(data.cr_language?.split(',').map(l => l.trim()) || []);
        setResidentDob(formatDate(data.cr_dob));
        setResidentEmail(data.cr_email || '');
        setSuretyName(data.co_name || '');
        setSuretyIc(data.co_ic_passport || '');
        setSuretyMobile(data.co_mobile || '');
        setSuretyAddress(data.co_address || '');
        setSuretyGender(data.co_gender === 'male' ? 'Male' : data.co_gender === 'female' ? 'Female' : '');
        setSuretyDob(formatDate(data.co_dob));
        setSpecialNotes(data.co_special_notes || '');
        setReplenishDiapers(data.diapers === 1 ? 'Yes' : data.diapers === 0 ? 'No' : '');
        setReplenishMedication(data.medication === 1 ? 'Yes' : data.medication === 0 ? 'No' : '');
        setReplenishMilkPowder(data.milk_powder === 1 ? 'Yes' : data.milk_powder === 0 ? 'No' : '');
        setFollowupHospital(data.follow_up_hospital || '');
        setDoctorName(data.follow_up_doctor_name || '');
        setDoctorContact(data.follow_up_doctor_contact || '');
        setEmergencyHospital(data.emergency_hospital || '');
        setEmergencyInstructions(data.emergency_special_instruction || '');
        setEmergencyContactName(data.emergency_name || '');
        setEmergencyContactMobile(data.emergency_mobile || '');
        setEmergencyContactRelationship(data.emergency_relationship || '');
    }, [data.id]);

    const isDirty = useMemo(() => {
        return (
            formatDate(initialState.date_of_admission) !== admissionDate ||
            (initialState.room_assigned || '') !== roomAssigned ||
            (initialState.cr_name || '') !== residentName ||
            (initialState.cr_ic_passport || '') !== residentIc ||
            (initialState.cr_mobile || '') !== residentMobile ||
            (initialState.cr_address || '') !== residentAddress ||
            (initialState.cr_height || '') !== residentHeightCm ||
            (initialState.cr_weight || '') !== residentWeight ||
            (initialState.cr_ambulatory === 1 ? 'Yes' : initialState.cr_ambulatory === 0 ? 'No' : '') !== residentAmbulatory ||
            (initialState.cr_gender === 'male' ? 'Male' : initialState.cr_gender === 'female' ? 'Female' : '') !== residentGender ||
            (initialState.cr_language || '') !== residentLanguages.join(', ') ||
            formatDate(initialState.cr_dob) !== residentDob ||
            (initialState.cr_email || '') !== residentEmail ||
            (initialState.co_name || '') !== suretyName ||
            (initialState.co_ic_passport || '') !== suretyIc ||
            (initialState.co_mobile || '') !== suretyMobile ||
            (initialState.co_address || '') !== suretyAddress ||
            (initialState.co_gender === 'male' ? 'Male' : initialState.co_gender === 'female' ? 'Female' : '') !== suretyGender ||
            formatDate(initialState.co_dob) !== suretyDob ||
            (initialState.co_special_notes || '') !== specialNotes ||
            (initialState.diapers === 1 ? 'Yes' : initialState.diapers === 0 ? 'No' : '') !== replenishDiapers ||
            (initialState.medication === 1 ? 'Yes' : initialState.medication === 0 ? 'No' : '') !== replenishMedication ||
            (initialState.milk_powder === 1 ? 'Yes' : initialState.milk_powder === 0 ? 'No' : '') !== replenishMilkPowder ||
            (initialState.follow_up_hospital || '') !== followupHospital ||
            (initialState.follow_up_doctor_name || '') !== doctorName ||
            (initialState.follow_up_doctor_contact || '') !== doctorContact ||
            (initialState.emergency_hospital || '') !== emergencyHospital ||
            (initialState.emergency_special_instruction || '') !== emergencyInstructions ||
            (initialState.emergency_name || '') !== emergencyContactName ||
            (initialState.emergency_mobile || '') !== emergencyContactMobile ||
            (initialState.emergency_relationship || '') !== emergencyContactRelationship ||
            (confirmationSignature !== null)
        );
    }, [initialState, admissionDate, roomAssigned, residentName, residentIc, residentMobile, residentAddress, residentHeightCm, residentWeight, residentAmbulatory, residentGender, residentLanguages, residentDob, residentEmail, suretyName, suretyIc, suretyMobile, suretyAddress, suretyGender, suretyDob, specialNotes, replenishDiapers, replenishMedication, replenishMilkPowder, followupHospital, doctorName, doctorContact, emergencyHospital, emergencyInstructions, emergencyContactName, emergencyContactMobile, emergencyContactRelationship, confirmationSignature]);


    useEffect(() => {
        if (residentDob) {
            const birthDate = new Date(residentDob);
            const today = new Date();
            if (isNaN(birthDate.getTime())) {
                setResidentAge('');
                return;
            }
            let age = today.getFullYear() - birthDate.getFullYear();
            const m = today.getMonth() - birthDate.getMonth();
            if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) age--;
            setResidentAge(age >= 0 ? age : '');
        } else {
            setResidentAge('');
        }
    }, [residentDob]);

    const handleResidentIcChange = (e: ChangeEvent<HTMLInputElement>) => {
        const icValue = e.target.value;
        setResidentIc(icValue);
        const dob = getDobFromIc(icValue);
        if (dob) {
            setResidentDob(dob);
        }
    };

    const handleSuretyIcChange = (e: ChangeEvent<HTMLInputElement>) => {
        const icValue = e.target.value;
        setSuretyIc(icValue);
        const dob = getDobFromIc(icValue);
        if (dob) {
            setSuretyDob(dob);
        }
    };

    const handleFollowupHospitalChange = (value: string) => {
        setFollowupHospital(value);
        setEmergencyHospital(value);
    };

    const handleDobChange = (e: ChangeEvent<HTMLInputElement>) => setResidentDob(e.target.value);
    
    const cmToFeetAndInches = (cm: number | string): string => {
        const cmValue = Number(cm);
        if (isNaN(cmValue) || cmValue <= 0) return '';
        const totalInches = cmValue / 2.54;
        const feet = Math.floor(totalInches / 12);
        const inches = Math.round(totalInches % 12);
        return feet === 0 && inches === 0 ? '' : `Approx. ${feet} ft ${inches} in`;
    };

    const handleFileSelect = (selectedFiles: File[]) => {
        const maxFiles = 5;
        const existingAttachments = Array.isArray(data.attachments) ? data.attachments : [];
        const existingPending = Array.isArray(pendingFiles) ? pendingFiles : [];

        const newFiles = selectedFiles.filter(
            (file) => 
                !existingAttachments.some(a => a.attachment_name === file.name) &&
                !existingPending.some(pf => pf.name === file.name && pf.size === file.size)
        );

        const totalFiles = existingAttachments.length + existingPending.length + newFiles.length;
        if (totalFiles > maxFiles) {
            alert(`You can only upload a maximum of ${maxFiles} files.`);
            newFiles.splice(maxFiles - (existingAttachments.length + existingPending.length));
        }

        if (newFiles.length > 0) {
            setPendingFiles(prev => [...prev, ...newFiles]);
            handleUploadFiles(newFiles);
        }
    };

    const handleRemovePendingFile = (fileName: string) => {
        setPendingFiles(prev => prev.filter(f => f.name !== fileName));
        setUploadProgress(prev => {
            const newProgress = { ...prev };
            delete newProgress[fileName];
            return newProgress;
        });
    };

    const uploadFileWithProgress = (file: File, url: string) => {
        return new Promise((resolve, reject) => {
            const xhr = new XMLHttpRequest();
            xhr.open('PUT', url);
            xhr.setRequestHeader('Content-Type', file.type);

            xhr.upload.onprogress = (event) => {
                if (event.lengthComputable) {
                    const progress = Math.round((event.loaded / event.total) * 100);
                    setUploadProgress(prev => ({
                        ...prev,
                        [file.name]: { status: 'uploading', progress }
                    }));
                }
            };

            xhr.onload = () => {
                if (xhr.status >= 200 && xhr.status < 300) {
                    setUploadProgress(prev => ({
                        ...prev,
                        [file.name]: { status: 'success', progress: 100 }
                    }));
                    resolve(xhr.response);
                } else {
                     setUploadProgress(prev => ({
                        ...prev,
                        [file.name]: { status: 'error', progress: 0 }
                    }));
                    reject(new Error(`Upload failed for ${file.name}: ${xhr.statusText}`));
                }
            };

            xhr.onerror = () => {
                setUploadProgress(prev => ({
                    ...prev,
                    [file.name]: { status: 'error', progress: 0 }
                }));
                reject(new Error(`Upload failed for ${file.name} due to a network error.`));
            };
            
            setUploadProgress(prev => ({
                ...prev,
                [file.name]: { status: 'uploading', progress: 0 }
            }));
            xhr.send(file);
        });
    };

    const handleUploadFiles = async (filesToUpload: File[]) => {
        if (filesToUpload.length === 0 || !decryptedCustomerId || decryptedCustomerId === 'Decryption Error') {
            return;
        }

        setIsUploading(true);
        try {
            const presignedUrlPayload = {
                payload: filesToUpload.map(file => ({
                    prefix: `${decryptedCustomerId}/rc_admission/attachments`,
                    filename: file.name,
                })),
            };

            const presignedUrlApi = `${API_URL}/resident/attachment`;
            const presignedUrlResponse = await fetch(presignedUrlApi, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'x-api-key': API_KEY_EN,
                },
                body: JSON.stringify(presignedUrlPayload),
            });

            if (!presignedUrlResponse.ok) throw new Error(`Failed to prepare file uploads. Status: ${presignedUrlResponse.status}`);
            
            const responseJson = await presignedUrlResponse.json();
            
            let urlList;
            if (Array.isArray(responseJson)) {
                urlList = responseJson;
            } else if (typeof responseJson === 'object' && responseJson !== null) {
                for (const key in responseJson) {
                    if (Object.prototype.hasOwnProperty.call(responseJson, key)) {
                        const value = responseJson[key];
                        if (Array.isArray(value)) {
                            urlList = value;
                            break;
                        }
                    }
                }
            }

            if (!Array.isArray(urlList)) throw new Error('File upload pre-signed URL response is not in the expected array format.');
            if (urlList.length !== filesToUpload.length) throw new Error(`Mismatch between requested files and received URLs.`);

            const successfulUploads: NewAttachment[] = [];
            for (let i = 0; i < filesToUpload.length; i++) {
                const file = filesToUpload[i];
                const uploadInfo = urlList[i];

                if (!uploadInfo || !uploadInfo.uploadUrl || !uploadInfo.downloadUrl) {
                    setUploadProgress(prev => ({...prev, [file.name]: { status: 'error', progress: 0 }}));
                    console.error(`Missing 'uploadUrl' or 'downloadUrl' for file: ${file.name}`);
                    continue;
                }
                
                try {
                    await uploadFileWithProgress(file, uploadInfo.uploadUrl);
                    successfulUploads.push({
                        attachment_name: file.name,
                        attachment_url_s3: uploadInfo.downloadUrl,
                    });
                } catch(uploadError) {
                    console.error(uploadError);
                }
            }

            if (successfulUploads.length > 0) {
                await onUpdate({ attachments: successfulUploads });
                setPendingFiles(prev => prev.filter(pf => !successfulUploads.some(sf => sf.attachment_name === pf.name)));
            }

            if (successfulUploads.length < filesToUpload.length) {
                alert('Some files failed to upload. Please try them again.');
            }

        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : String(error);
            alert(`File upload failed. Please check the console for details. Error: ${errorMessage}`);
            filesToUpload.forEach(file => {
                 setUploadProgress(prev => ({...prev, [file.name]: { status: 'error', progress: 0 }}));
            });
        } finally {
            setIsUploading(false);
        }
    };

    const uploadSignature = async (dataUrl: string, type: string): Promise<string | null> => {
        const LOG_PREFIX = `[Signature Upload - Admission]`;
        console.log(`${LOG_PREFIX} Starting for type: ${type}`);
    
        if (!decryptedCustomerId || decryptedCustomerId === 'Decryption Error') {
            const msg = `${LOG_PREFIX} FAILED: Cannot upload signature without a valid customer ID.`;
            reportError('Cannot upload signature: missing customer ID.');
            console.error(msg);
            return null;
        }
        console.log(`${LOG_PREFIX} Customer ID found: ${decryptedCustomerId}`);
    
        try {
            const filename = `${type}.jpg`;
            const prefix = `${decryptedCustomerId}/${type}/signatures`;
            
            const presignedUrlPayload = {
                payload: [{ prefix, filename }],
            };
            console.log(`${LOG_PREFIX} Requesting pre-signed URL with payload:`, JSON.stringify(presignedUrlPayload, null, 2));
    
            const presignedUrlApi = `${API_URL}/resident/attachment`;
            const presignedUrlResponse = await fetch(presignedUrlApi, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'x-api-key': API_KEY_EN },
                body: JSON.stringify(presignedUrlPayload),
            });
            
            if (!presignedUrlResponse.ok) {
                const errorText = await presignedUrlResponse.text();
                const msg = `${LOG_PREFIX} FAILED: Pre-signed URL request failed with status ${presignedUrlResponse.status}. Response: ${errorText}`;
                console.error(msg);
                reportError('Could not get upload URL for signature. Please retry.');
                throw new Error('Failed to get pre-signed URL for signature.');
            }
            
            const responseData = await presignedUrlResponse.json();
            console.log(`${LOG_PREFIX} Received pre-signed URL response:`, JSON.stringify(responseData, null, 2));

            const filePath = `${prefix}/${filename}`;
            const responseArray = responseData[filePath];
            const uploadInfo = Array.isArray(responseArray) && responseArray.length > 0 ? responseArray[0] : null;

            if (!uploadInfo || !uploadInfo.uploadUrl || !uploadInfo.downloadUrl) {
                const msg = `${LOG_PREFIX} FAILED: Invalid pre-signed URL response. 'uploadUrl' or 'downloadUrl' missing.`;
                console.error(msg);
                reportError('Received an invalid upload URL for signature. Please retry.');
                throw new Error('Invalid pre-signed URL response for signature.');
            }
            console.log(`${LOG_PREFIX} Pre-signed URL received. Uploading to: ${uploadInfo.uploadUrl}`);
    
            const file = dataURLtoFile(dataUrl, filename);
            console.log(`${LOG_PREFIX} Converted data URL to file: ${file.name}, size: ${file.size} bytes, type: ${file.type}`);
            
            const uploadResponse = await fetch(uploadInfo.uploadUrl, {
                method: 'PUT',
                body: file,
                headers: { 'Content-Type': file.type },
            });
            
            if (!uploadResponse.ok) {
                const errorText = await uploadResponse.text();
                const msg = `${LOG_PREFIX} FAILED: S3 upload failed with status ${uploadResponse.status}. Response: ${errorText}`;
                console.error(msg);
                reportError('Signature upload failed. Please retry.');
                throw new Error('Signature upload to S3 failed.');
            }
    
            console.log(`${LOG_PREFIX} SUCCESS: Upload successful for type: ${type}. Download URL: ${uploadInfo.downloadUrl}`);
            return uploadInfo.downloadUrl;
        } catch (error) {
            console.error(`${LOG_PREFIX} FAILED: An error occurred during the process for type ${type}:`, error);
            reportError('Failed to save signature. Please try again.');
            return null;
        }
    };
    
    const performSave = async () => {
        setIsUploadingSignatures(true);
        const newSignatures: NewSignature[] = [];

        if (!isConfirmationReady && !displayConfirmationSignature && !confirmationSignature) {
            reportError('Enter Surety name and IC/Passport to enable signing.');
            setIsUploadingSignatures(false);
            throw new Error('Signature prerequisites missing.');
        }
        
        try {
            if (confirmationSignature && confirmationSignature.startsWith('data:image')) {
                const url = await uploadSignature(confirmationSignature, 'admission_co_type');
                if (url) {
                    newSignatures.push({
                        type: 'admission_co_type',
                        signature_url_s3: url,
                        name: suretyName,
                        ic_passport: suretyIc,
                        date: new Date().toISOString(),
                    });
                } else {
                    throw new Error("Signature upload failed, aborting save.");
                }
            }
        } catch (e) {
            setIsUploadingSignatures(false);
            throw e; // Propagate error to stop execution
        }

        setIsUploadingSignatures(false);

        const payload: Partial<Omit<AdmissionData, 'items' | 'attachments' | 'signatures'>> & { signatures?: NewSignature[] } = {
            date_of_admission: admissionDate,
            room_assigned: roomAssigned,
            cr_name: residentName,
            cr_ic_passport: residentIc,
            cr_mobile: residentMobile,
            cr_address: residentAddress,
            cr_height: Number(residentHeightCm) || null,
            cr_weight: Number(residentWeight) || null,
            cr_ambulatory: residentAmbulatory === 'Yes' ? 1 : (residentAmbulatory === 'No' ? 0 : null),
            cr_gender: residentGender.toLowerCase(),
            cr_language: residentLanguages.join(', '),
            cr_dob: residentDob,
            cr_email: residentEmail,
            co_name: suretyName,
            co_ic_passport: suretyIc,
            co_mobile: suretyMobile,
            co_address: suretyAddress,
            co_gender: suretyGender.toLowerCase(),
            co_dob: suretyDob,
            co_special_notes: specialNotes,
            diapers: replenishDiapers === 'Yes' ? 1 : (replenishDiapers === 'No' ? 0 : null),
            medication: replenishMedication === 'Yes' ? 1 : (replenishMedication === 'No' ? 0 : null),
            milk_powder: replenishMilkPowder === 'Yes' ? 1 : (replenishMilkPowder === 'No' ? 0 : null),
            follow_up_hospital: followupHospital,
            follow_up_doctor_name: doctorName,
            follow_up_doctor_contact: doctorContact,
            emergency_hospital: emergencyHospital,
            emergency_special_instruction: emergencyInstructions,
            emergency_name: emergencyContactName,
            emergency_mobile: emergencyContactMobile,
            emergency_relationship: emergencyContactRelationship,
        };
        
        if (newSignatures.length > 0) {
            payload.signatures = newSignatures;
        }

        try {
            await onUpdate(payload);
            if (newSignatures.length > 0) {
                setConfirmationSignature(null);
            }
        } catch (error) {
            console.error("Failed to save admission data:", error);
            throw error;
        }
    };

    const handleSaveSubmit = async (e: FormEvent) => {
        e.preventDefault();
        try {
            await performSave();
        } catch (error) {
            reportError('Save failed. Please fix the highlighted issues and retry.');
        }
    };

    const handleNextClick = async () => {
        try {
            if (isDirty) {
                await performSave();
            }
            onContinue();
        } catch (error) {
            // Save failed, so we don't navigate.
            reportError('Save failed, so navigation was cancelled. Please resolve errors and try again.');
        }
    };

    const SaveButton = (
        <button type="submit" disabled={isSaving || isUploading || isUploadingSignatures || !isDirty} className="w-full sm:w-auto px-8 py-3 bg-slate-600 text-white font-bold rounded-lg shadow-md hover:bg-slate-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-slate-500 transition-all duration-200 flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed">
            {isSaving || isUploading || isUploadingSignatures ? 'Saving...' : 'Save'}
        </button>
    );

    const NextButton = (
        <button type="button" onClick={handleNextClick} disabled={isSaving || isUploading || isUploadingSignatures} className="w-full sm:w-auto px-8 py-3 bg-brand-primary text-white font-bold rounded-lg shadow-md hover:bg-brand-primary/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-primary transition-all duration-200 flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed">
            {isSaving || isUploading || isUploadingSignatures ? 'Saving...' : 'Next'}
        </button>
    );

    const actionButtons = <div className="flex flex-col sm:flex-row items-center gap-4 w-full sm:w-auto">{SaveButton}{NextButton}</div>;

    const commonFieldProps = {
        isPrintView,
    };

    const formContent = (
        <>
        <Section title="Admission Info">
            <FormField {...commonFieldProps} label="Date of Admission" name="admissionDate" type="date" value={admissionDate} onChange={(e) => setAdmissionDate(e.target.value)} readOnly={isClientView || isPrintView} highlight={!isClientView && isFieldEmpty(admissionDate)} isStaffInput={true} />
            <FormField {...commonFieldProps} label="Room Assigned" name="roomAssigned" type="text" value={roomAssigned} onChange={(e) => setRoomAssigned(e.target.value)} readOnly={isClientView || isPrintView} highlight={!isClientView && isFieldEmpty(roomAssigned)} isStaffInput={true} />
        </Section>
        
         <Section title={
            <div className="flex justify-between items-center flex-wrap gap-2">
                <span>Resident General Information (Care Recipient)</span>
                {decryptedCustomerId && (
                    <span className="text-sm font-medium text-slate-500 bg-slate-100 px-3 py-1 rounded-full print:hidden">
                        Care ID: {decryptedCustomerId}
                    </span>
                )}
            </div>
        }>
            <FormField {...commonFieldProps} label="Full Name" name="residentName" value={residentName} onChange={(e) => setResidentName(e.target.value)} readOnly={isPrintView} highlight={isClientView && isFieldEmpty(residentName)} />
            <FormField {...commonFieldProps} label="IC / Passport" name="residentIc" value={residentIc} onChange={handleResidentIcChange} readOnly={isPrintView} highlight={isClientView && isFieldEmpty(residentIc)} />
            <PhoneField {...commonFieldProps} label="Mobile" name="residentMobile" value={residentMobile} onChange={setResidentMobile} highlight={isClientView && isFieldEmpty(residentMobile)} />
            <AddressField {...commonFieldProps} label="Address" name="residentAddress" fullWidth value={residentAddress} onChange={setResidentAddress} readOnly={isPrintView} highlight={isClientView && isFieldEmpty(residentAddress)} />
            <div>
                <FormField {...commonFieldProps} label="Height (cm)" name="residentHeight" type="number" value={residentHeightCm} onChange={(e) => setResidentHeightCm(e.target.value)} readOnly={isPrintView} highlight={isClientView && isFieldEmpty(residentHeightCm)} />
                {!isPrintView && cmToFeetAndInches(residentHeightCm) && <p className="text-xs text-slate-500 mt-1.5 px-1">{cmToFeetAndInches(residentHeightCm)}</p>}
            </div>
            <FormField {...commonFieldProps} label="Weight (kg)" name="residentWeight" type="number" value={residentWeight} onChange={(e) => setResidentWeight(e.target.value)} readOnly={isPrintView} highlight={isClientView && isFieldEmpty(residentWeight)} />
            <RadioGroupField {...commonFieldProps} label="Ambulatory" name="residentAmbulatory" options={['Yes', 'No']} value={residentAmbulatory} onChange={(e) => setResidentAmbulatory(e.target.value)} highlight={isClientView && isFieldEmpty(residentAmbulatory)} />
            <RadioGroupField {...commonFieldProps} label="Gender" name="residentGender" options={['Male', 'Female']} value={residentGender} onChange={(e) => setResidentGender(e.target.value)} highlight={isClientView && isFieldEmpty(residentGender)} />
            <CheckboxGroupField {...commonFieldProps} label="Languages" name="residentLanguages" options={['English', 'Chinese', 'Malay', 'Hindi', 'Others']} fullWidth value={residentLanguages} onChange={setResidentLanguages} highlight={isClientView && isFieldEmpty(residentLanguages)} />
            <FormField {...commonFieldProps} label="DOB" name="residentDob" type="date" value={residentDob} onChange={handleDobChange} readOnly={isPrintView} highlight={isClientView && isFieldEmpty(residentDob)} />
            <FormField {...commonFieldProps} label="Age" name="residentAge" readOnly value={residentAge} />
            <FormField {...commonFieldProps} label="Email" name="residentEmail" type="email" value={residentEmail} onChange={(e) => setResidentEmail(e.target.value)} readOnly={isPrintView} highlight={isClientView && isFieldEmpty(residentEmail)} />
        </Section>
        
        <Section title="Resident's Surety / Representative (Care Owner)">
            <FormField {...commonFieldProps} label="Full Name" name="suretyName" value={suretyName} onChange={(e) => setSuretyName(e.target.value)} readOnly={isPrintView} highlight={isClientView && isFieldEmpty(suretyName)} />
            <FormField {...commonFieldProps} label="IC / Passport" name="suretyIc" value={suretyIc} onChange={handleSuretyIcChange} readOnly={isPrintView} highlight={isClientView && isFieldEmpty(suretyIc)} />
            <PhoneField {...commonFieldProps} label="Mobile" name="suretyMobile" value={suretyMobile} onChange={setSuretyMobile} highlight={isClientView && isFieldEmpty(suretyMobile)} />
            <AddressField {...commonFieldProps} label="Address" name="suretyAddress" fullWidth value={suretyAddress} onChange={setSuretyAddress} readOnly={isPrintView} highlight={isClientView && isFieldEmpty(suretyAddress)} />
            <RadioGroupField {...commonFieldProps} label="Gender" name="suretyGender" options={['Male', 'Female']} value={suretyGender} onChange={(e) => setSuretyGender(e.target.value)} highlight={isClientView && isFieldEmpty(suretyGender)} />
            <FormField {...commonFieldProps} label="DOB" name="suretyDob" type="date" value={suretyDob} onChange={(e) => setSuretyDob(e.target.value)} readOnly={isPrintView} highlight={isClientView && isFieldEmpty(suretyDob)} />
            <TextAreaField {...commonFieldProps} label="Special Notes" name="specialNotes" fullWidth value={specialNotes} onChange={(e) => setSpecialNotes(e.target.value)} highlight={isClientView && isFieldEmpty(specialNotes)} />
        </Section>
        
        <FileUpload 
            title="Documents Required" 
            maxFiles={5} 
            attachments={data.attachments} 
            pendingFiles={pendingFiles}
            uploadProgress={uploadProgress}
            onFileSelect={handleFileSelect}
            onRemovePendingFile={handleRemovePendingFile}
            isPrintView={isPrintView}>
            <div className="text-sm text-slate-600 mb-6 print:mb-2 print:text-xs">
                <p className="font-semibold text-slate-700">Please attach the following documents:</p>
                <ol className="list-decimal list-inside mt-2 space-y-1">
                    <li>Resident's Photocopy I/C</li>
                    <li>Passport Front Page</li>
                    <li>Surety Contact Person Photocopy I/C</li>
                </ol>
            </div>
        </FileUpload>
        
        <Section title="Care Information (Replenishment)">
            <RadioGroupField {...commonFieldProps} label="Diapers" name="replenishDiapers" options={['Yes', 'No']} value={replenishDiapers} onChange={(e) => setReplenishDiapers(e.target.value)} highlight={isClientView && isFieldEmpty(replenishDiapers)} />
            <RadioGroupField {...commonFieldProps} label="Medication" name="replenishMedication" options={['Yes', 'No']} value={replenishMedication} onChange={(e) => setReplenishMedication(e.target.value)} highlight={isClientView && isFieldEmpty(replenishMedication)} />
            <RadioGroupField {...commonFieldProps} label="Milk Powder" name="replenishMilkPowder" options={['Yes', 'No']} value={replenishMilkPowder} onChange={(e) => setReplenishMilkPowder(e.target.value)} highlight={isClientView && isFieldEmpty(replenishMilkPowder)} />
        </Section>
        
        <Section title="Follow-up Hospital / Medical Centre">
            <ComboBoxField 
                {...commonFieldProps}
                label="Hospital" 
                name="followupHospital" 
                options={hospitalsInMalaysia}
                placeholder="Select or type hospital name"
                value={followupHospital}
                onChange={handleFollowupHospitalChange}
                highlight={isClientView && isFieldEmpty(followupHospital)}
            />
            <FormField {...commonFieldProps} label="Doctor's Name" name="doctorName" value={doctorName} onChange={(e) => setDoctorName(e.target.value)} readOnly={isPrintView} highlight={isClientView && isFieldEmpty(doctorName)} />
            <PhoneField {...commonFieldProps} label="Doctor's Contact" name="doctorContact" value={doctorContact} onChange={setDoctorContact} highlight={isClientView && isFieldEmpty(doctorContact)} />
        </Section>
        
        <div className="py-8 border-b border-slate-200 print:border-none print:py-4 print:break-inside-avoid">
            <h3 className="text-xl font-semibold text-slate-800 mb-6 print:mb-3 print:text-lg">Emergency</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-6 print:grid-cols-3 print:gap-y-2 print:gap-x-4">
                <ComboBoxField 
                    {...commonFieldProps}
                    label="Hospital" 
                    name="emergencyHospital" 
                    options={hospitalsInMalaysia}
                    placeholder="Select or type hospital name"
                    value={emergencyHospital}
                    onChange={setEmergencyHospital}
                    highlight={isClientView && isFieldEmpty(emergencyHospital)}
                />
                <TextAreaField {...commonFieldProps} label="Special Instruction" name="emergencyInstructions" fullWidth value={emergencyInstructions} onChange={(e) => setEmergencyInstructions(e.target.value)} highlight={isClientView && isFieldEmpty(emergencyInstructions)} />
            </div>
            <h4 className="text-lg font-semibold text-slate-700 mt-8 mb-4 print:mt-4 print:mb-2 print:text-base">Family Emergency Contact for Hospital Admission</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-6 print:grid-cols-3 print:gap-y-2 print:gap-x-4">
                <FormField {...commonFieldProps} label="Full Name" name="emergencyContactName" value={emergencyContactName} onChange={(e) => setEmergencyContactName(e.target.value)} readOnly={isPrintView} highlight={isClientView && isFieldEmpty(emergencyContactName)} />
                <PhoneField {...commonFieldProps} label="Mobile" name="emergencyContactMobile" value={emergencyContactMobile} onChange={setEmergencyContactMobile} highlight={isClientView && isFieldEmpty(emergencyContactMobile)} />
                <FormField {...commonFieldProps} label="Relationship" name="emergencyContactRelationship" value={emergencyContactRelationship} onChange={(e) => setEmergencyContactRelationship(e.target.value)} readOnly={isPrintView} highlight={isClientView && isFieldEmpty(emergencyContactRelationship)} />
            </div>
        </div>

        <div className="py-8 print:py-4 print:break-inside-avoid">
            <h3 className="text-xl font-semibold text-slate-800 mb-6 print:mb-3 print:text-lg">Confirmation</h3>
            <div className="bg-brand-primary-light p-6 rounded-lg border border-brand-primary/20 space-y-4 print:bg-transparent print:p-0 print:border-none print:space-y-2">
                <p className="text-slate-800 font-medium print:text-xs">I hereby confirm that the information given in this form is true, complete and accurate.</p>
                <p className="text-slate-800 font-medium print:text-xs">In case of any emergency where ambulance service is needed, I will also pay for the cost of the ambulance.</p>
                <div className="pt-4 print:pt-2">
                    <h4 className="text-base font-semibold text-slate-700 mb-4 print:mb-2 print:text-sm">Signed By:</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6 items-end print:grid-cols-2 print:gap-x-4 print:gap-y-2">
                        <div>
                            <SignaturePad isPrintView={isPrintView} label="Signature" signature={confirmationSignature || displayConfirmationSignature} onSign={setConfirmationSignature} readOnly={!!displayConfirmationSignature || !isConfirmationReady} highlight={isClientView && isFieldEmpty(confirmationSignature)} />
                            {!isConfirmationReady && !displayConfirmationSignature && !isPrintView && (
                                <p className="text-xs text-red-500 mt-1.5 px-1">
                                    Please ensure Surety's Full Name and IC/Passport are filled in the section above before signing.
                                </p>
                            )}
                        </div>
                        <div className="space-y-6 print:space-y-2">
                            <FormField {...commonFieldProps} label="Full Name" name="confirmationName" value={suretyName} readOnly />
                            <FormField {...commonFieldProps} label="IC / Passport" name="confirmationIc" value={suretyIc} readOnly />
                        </div>
                    </div>
                </div>
            </div>
        </div>
        </>
    );

    if (isPrintView) {
        return <div className="animate-fade-in">{formContent}</div>;
    }

    return (
        <div className="animate-fade-in">
            <form className="space-y-4" onSubmit={handleSaveSubmit}>
                <PageHeader 
                    title="Resident Admission"
                    subtitle="Please fill in the details below to start the admission process."
                    action={actionButtons}
                    isPrintView={isPrintView}
                />
                
                {formContent}
                
                <div className="pt-6 flex justify-end">
                    {actionButtons}
                </div>
            </form>
        </div>
    );
};

export default AdmissionPage;